import React, {CSSProperties, ReactNode} from 'react';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import Tooltip from 'components/pure/utils/Tooltip';
import User from 'records/User';
import {CircledUserIcon} from '@components/icon/CircledUserIcon';

import 'styles/utils/UserAvatar.styl';

interface UserAvatarProps {
  user: User | undefined;
  enableTooltip?: boolean;
  tooltipDirection?: 'top' | 'bottom' | 'right' | 'left';
  tooltipText?: ReactNode;
  overrideUrl?: boolean;
  fillBackground?: boolean;
  className?: string;
  /** If true, the avatar is loaded with cache busting. Use this only when editing the avatar. */
  sensitive?: boolean;
}

interface UserAvatarState {
  showTooltip: boolean;
  initialsFallback: boolean;
}
// Types of avatars that can be rendered
const TYPE_IMAGE = 'TYPE_IMAGE';
const TYPE_INITIALS = 'TYPE_INITIALS';
const TYPE_ANON = 'TYPE_ANON';

/**
 * UserAvatar component.
 *
 * It can render either:
 *  1) an <img> with the user's avatar
 *  2) a <div> with the user's initials and color
 *  3) a default <img> if none of the above are provided
 *
 * It can optionally render a tooltip with user information.
 */
export default class UserAvatar extends React.Component<UserAvatarProps, UserAvatarState> {
  // generate random unique string to use as the tooltip id
  tooltipId;
  // ref holder
  avatar;

  constructor(props) {
    super(props);
    this.state = {
      showTooltip: false,
      initialsFallback: false,
    };

    this.tooltipId = uniqueId('user_avatar_');
  }

  /**
   * Update state to show tooltip
   */
  showTooltip = () => {
    this.setState({
      showTooltip: true,
    });
  };

  /**
   * Update state to hide tooltip
   */
  hideTooltip = () => {
    // remove all text selected, so it doesn't show the next
    // time the tooltip opens.
    window.getSelection()?.removeAllRanges();

    this.setState({
      showTooltip: false,
    });
  };

  /**
   * Return type of avatar that will be rendered based on the props provided
   * @returns {String}
   */
  getType() {
    const user = this.props.user;
    const initialsFallback = this.state.initialsFallback;

    if (!initialsFallback && user && user.avatar) {
      return TYPE_IMAGE;
    } else if (user && user.color && user.firstName && user.lastName) {
      return TYPE_INITIALS;
    } else {
      return TYPE_ANON;
    }
  }

  /**
   * Return true if the user tooltip should be rendered
   * based on the props provided
   * @returns {boolean}
   */
  shouldRenderTooltip() {
    const avatarType = this.getType();
    const enableTooltip = this.props.enableTooltip;
    const user = this.props.user;
    const hasDisplayName = user?.displayName ?? user?.getFullName?.();
    const hasAssignedTooltipText = this.props.tooltipText;
    return (
      enableTooltip && ((avatarType !== TYPE_ANON && hasDisplayName) || hasAssignedTooltipText)
    );
  }

  /**
   * Return props that should be applied to the rendered avatar elements
   * if the tooltip will be rendered.
   * @returns {Object}
   */
  getTooltipTargetProps() {
    const tooltipTargetProps = {
      onMouseEnter: this.showTooltip,
      onMouseLeave: this.hideTooltip,
    };

    return this.shouldRenderTooltip() ? tooltipTargetProps : {};
  }

  /**
   * Render an <img> with the user's avatar or a default svg
   **/
  renderImageAvatar() {
    const {user, overrideUrl, sensitive} = this.props;
    const tooltipProps = this.getTooltipTargetProps();

    const url = overrideUrl
      ? user?.avatar
      : `/api/assets/avatar/${user?.id}` + (sensitive ? `?${Date.now()}` : ''); // cache busting.

    if (this.state.initialsFallback) {
      return <CircledUserIcon {...tooltipProps} className="avatar-inner avatar-image" />;
    }

    return (
      <img
        {...tooltipProps}
        alt="avatar"
        className="avatar-inner avatar-image"
        src={url}
        onError={() => this.setState({initialsFallback: true})}
      />
    );
  }

  /**
   * Render a <div> with the user's initials and color.
   * Note that ReactFitText is used to make the text size proportional to the avatar size
   **/
  renderInitialsAvatar() {
    const user = this.props.user;
    const firstLetter = user?.firstName.substring(0, 1).toUpperCase();
    const secondLetter = user?.lastName.substring(0, 1).toUpperCase();
    const initials = `${firstLetter}${secondLetter}`;
    const color = `#${user?.color}`;
    const tooltipProps = this.getTooltipTargetProps();

    const style: CSSProperties = {borderColor: color};
    if (this.props.fillBackground) {
      style.background = color;
      style.border = '1px solid white';
    }

    return (
      <div className="avatar-inner avatar-initials" style={style} {...tooltipProps}>
        <svg viewBox="-14 1 28 18" style={{width: '90%', height: '90%'}}>
          <text x="0" y="15" fill={color} textAnchor={'middle'}>
            {initials}
          </text>
        </svg>
      </div>
    );
  }

  /**
   * Render an avatar element based on the avatar type
   */
  renderAvatar() {
    const avatarType = this.getType();
    switch (avatarType) {
      case TYPE_IMAGE:
        return this.renderImageAvatar();
      case TYPE_INITIALS:
        return this.renderInitialsAvatar();
      default:
        return <CircledUserIcon className="avatar-inner avatar-image" />;
    }
  }

  tooltipTextToRender() {
    const {tooltipText, user} = this.props;
    return tooltipText ? tooltipText : user?.getFullName ? user.getFullName() : user?.displayName;
  }

  /**
   * Render the user tooltip element, if it is allowed to render
   */
  renderTooltip() {
    if (this.shouldRenderTooltip()) {
      return (
        <Tooltip
          id={this.tooltipId}
          effect="solid"
          place={this.props.tooltipDirection}
          class="user-tooltip"
          renderToLayer={true}
          active={this.state.showTooltip}
          parent={this.avatar}>
          <div className="user-tooltip-content">
            <div className="user-name">{this.tooltipTextToRender()}</div>
          </div>
        </Tooltip>
      );
    }
  }

  render() {
    const className = classNames('avatar', this.props.className);

    // Render a container for the avatar and the user tooltip.
    // Note that the container itself is the tooltip target, this is to
    // avoid issues with the positioning of the tooltips because of
    // the avatar borders.
    return (
      <div
        className={className}
        data-tip
        data-for={this.tooltipId}
        ref={avatar => {
          this.avatar = avatar;
        }}>
        {this.renderAvatar()}
        {this.renderTooltip()}
      </div>
    );
  }
}
